import { useEffect, useRef, useState } from 'react';

const problems = [
  {
    icon: 'ri-time-line',
    title: '会議で何も決まらない',
    body: '1時間話し合っても結論が出ず、「次回また議論しましょう」で終わる。',
  },
  {
    icon: 'ri-checkbox-blank-circle-line',
    title: '決めても実行されない',
    body: '会議でOKが出ても、誰も動かず、気づけば忘れられている。',
  },
  {
    icon: 'ri-repeat-line',
    title: '同じ議題が繰り返される',
    body: '先月も、先々月も同じテーマ。会議が消化試合になっている。',
  },
  {
    icon: 'ri-user-unfollow-line',
    title: '管理職が疲弊している',
    body: '会議の準備・参加・フォローで、本来の仕事ができない管理職が続出。',
  },
  {
    icon: 'ri-hourglass-line',
    title: '会議時間が長すぎる',
    body: '週の稼働時間の30%以上が会議に費やされ、生産性が下がる一方。',
  },
];

export default function ProblemSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="problem" className="py-24 px-4 md:px-10 bg-white">
      <div className="max-w-5xl mx-auto">
        <div
          className="text-center mb-14 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <span
            className="inline-block px-3 py-1 text-xs font-bold tracking-widest uppercase text-[#C0392B] border border-[#C0392B]/30 rounded-full mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Problem
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            あなたの会社の会議、<br className="hidden md:block" />
            こんな状態ではありませんか？
          </h2>
          <p className="text-sm text-gray-500" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            従業員30〜300名の中小企業の経営者・管理職が手間に感じる共通の「壁会議」
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {problems.map((p, i) => (
            <div
              key={i}
              className={`p-6 rounded-lg border transition-all duration-700 ${
                i === 4 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              style={{
                borderColor: '#e5e5e5',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transitionDelay: `${i * 0.08}s`,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C0392B]/10 mb-4">
                <i className={`${p.icon} text-xl text-[#C0392B]`} />
              </div>
              <h3
                className="text-base font-bold text-gray-900 mb-2"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                {p.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                {p.body}
              </p>
            </div>
          ))}
        </div>

        {/* CTA mid */}
        <div
          className="mt-12 text-center transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '0.5s' }}
        >
          <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            1つでも当てはまる方 — 根本的な解決策があります
          </p>
          <button
            onClick={() => document.querySelector('#root-cause')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 text-sm font-bold text-[#C0392B] border border-[#C0392B]/30 px-6 py-3 rounded-md hover:bg-[#C0392B]/5 transition-colors cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            原因を見る <i className="ri-arrow-down-line" />
          </button>
        </div>
      </div>
    </section>
  );
}
